// Core components
import { AfterViewInit, Component, EventEmitter, OnDestroy, OnInit } from '@angular/core';

// Third party components
import { FieldType } from '@ngx-formly/material';
import { concat, Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { ListApiResponseV2 } from 'src/app/shared/interfaces/_base/api-response.interface';

// Custom components

// @see https://stackblitz.com/edit/angular-g9txuh-zysdfi?file=src/app/autocomplete-type.component.ts

@Component({
  selector: 'app-formly-autocomplete-type',
  template: `
    <!-- [trackByFn]="trackByFn" -->
    <div 
      style="letter-spacing: 1px; font-weight: bolder; padding-bottom: 2px; color: #e56c11;"
       [ngClass]="{'d-none': !to.customLabel }"
    >
      <span>{{ to.customLabel }}</span> <span *ngIf="to.required">*</span>
    </div>
    <ng-select
      [items]="options$ | async"
      [bindValue]="to.bindValue"
      [bindLabel]="to.bindLabel"
      [multiple]="to.multiple"
      [hideSelected]="to.hideSelected"
      [typeahead]="optionsInput$"
      [loading]="optionsLoading"
      [(ngModel)]="value"
      [placeholder]="to.placeholder"
      [minTermLength]="to.minTermLength"
      [typeToSearchText]="to.typeToSearchText"
      [clearAllText]="to.clearAllText"
      [addTag]="to.addTag"
      [addTagText]="to.addTagText"
      [notFoundText]="to.notFoundText"
      [loadingText]="to.loadingText"
      (change)="onChange($event)"
      [disabled]="to.disabled"
      [readonly]="to.readonly"
      [required]="to.required"
      [ngClass]="{'autocomplete-invalid': !value && to.required }"
      >
    </ng-select>
    <div *ngIf="!value && to.required" class="mat-error" style="font-size: 75%; padding: 0.4375em 0;">
      Il campo è richiesto
    </div>
  `,
})
export class AutocompleteTypeComponent extends FieldType implements OnInit, AfterViewInit, OnDestroy {
  onDestroy$ = new Subject<void>();
  search$ = new EventEmitter();
  options$: Observable<any> = new Observable();
  optionsLoading = false;
  optionsInput$: Subject<string> = new Subject<string>();
  value;

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    super.ngOnInit();
    this.value = this.formControl.value;

    if (!this.to.minTermLength) {
      this.to.minTermLength = 0;
    }

    this.options$ = this.buildOptions$();
  }

  /**
   * Build the options$ observable, pre-populated with the field's initial
   * values (if any) and then wired up to search on every optionsInput$ emission.
   *
   * @since 1.0.0
   */
  private buildOptions$(): Observable<any> {
    let initialValues = [];
    if (this.to.initialValues && this.to.initialValues.length) {
      initialValues = this.to.initialValues;
    }

    return concat(
      of(initialValues),
      this.optionsInput$.pipe(
        takeUntil(this.onDestroy$), // auto-unsubscribe on onDestroy()
        debounceTime(500), // max 1 query every 350 milliseconts
        tap(() => this.optionsLoading = true), // Start loader
        distinctUntilChanged(), // Eliminate duplicate values
        switchMap((term: string) => {
          // An empty/cleared term must yield no results rather than falling
          // through to the backend's default (unfiltered) listing
          if (!term || !term.trim()) {
            this.optionsLoading = false;
            return of([]);
          }

          const options: any = {
            general: term,
          };
          if (typeof this.to.getAll !== 'undefined') {
            options.getAll = this.to.getAll;
          }
          if (!this.to.search$) {
            this.optionsLoading = false
            if (options && Array.isArray(options) && options.length) {
              return options;
            }
            return [];
          }
          const limit = typeof this.to.limit !== 'undefined' ? this.to.limit : 10;
          return this.to.search$(0, limit, '', '', options)
            .pipe(
              map((res: ListApiResponseV2) => {
                if (res.status) {
                  if (res.data.items) {
                    return res.data.items;
                  }
                  if (res.data) {
                    return res.data;
                  }
                }
                return [];
              }),
              catchError(() => of([])), // empty list on error
              tap(() => this.optionsLoading = false)
            )
        })
      )
    )
  }

  /**
   * Handle afterViewInit of this component
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  /**
   * Handle ngOnDestroy of this component
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.onDestroy$.complete();
  }

  /**
   * Fired every time model data changes
   *
   * @param {*} data new model data
   */
  onChange(data: any): void {
    if (typeof this.to.callback === 'function') {
      this.to.callback(data);
    }

    // Some fields (@see to.resetOnSelect) want the dropdown's option list
    // cleared back to its initial state right after a selection, instead of
    // continuing to show the last search's stale results. Rebuilding the full
    // pipeline (rather than swapping in a plain of(initialValues)) keeps it
    // wired up to optionsInput$ so subsequent typing still triggers a search.
    if (this.to.resetOnSelect && data) {
      this.options$ = this.buildOptions$();
    }
  }
}
